import { isEqual } from 'lodash';
import React from 'react';

import * as ManifestUI from '@project44/manifest-ds';
import { designTokens, reactIcons as Icons } from '@project44/manifest-ds';

import { IFormClient } from '../../../../../../external/UIFormClient/domain/interface';
import { HTMLFormElementsFactory } from '../../../../../../external/UILibrary/infrastructure/factories/HTMLFormElements.factory';
import SetPasswordInstructions from './SetPasswordInstructions';
import {
  NewPasswordValidationType,
  UserPasswordComponent,
} from './UserPassword.component';

export type TUserPasswordView = {
  propertyName: string;
  formClient: IFormClient;
  isLoading?: boolean;
  userPasswordComponent: UserPasswordComponent;
};

export const UserPasswordView: React.FC<TUserPasswordView> = ({
  userPasswordComponent,
  propertyName,
  formClient,
  isLoading,
}) => {
  const {
    radioGroupConfig: { radioGroupProps, radioButtons },
  } = userPasswordComponent;

  const { spaceLarge, spaceSmall } = ManifestUI.designTokens;

  const [selectedView, setSelectedView] = React.useState<string>(
    radioGroupProps?.value || '',
  );

  const [showPassword, setShowPassword] = React.useState<boolean>(false);

  const RenderedIcon = showPassword ? Icons.EyeHidden : Icons.EyeOpen;

  const [passwordValidations, setPasswordValidations] =
    React.useState<NewPasswordValidationType>(
      UserPasswordComponent.newPasswordValidationState,
    );

  React.useEffect(() => {
    const isNotDelegatedPasswordCreation = selectedView !== 'DELEGATED';
    const isInputFieldTouched: boolean = !!formClient.touched[propertyName];

    const shouldValidate: boolean = new Array<boolean>(
      isNotDelegatedPasswordCreation,
      isInputFieldTouched,
    ).every((item) => item);

    const passwordValidationType = UserPasswordComponent.passwordValidate(
      formClient.values[propertyName],
      true,
      passwordValidations,
    );

    if (
      shouldValidate &&
      !isEqual(passwordValidations, passwordValidationType)
    ) {
      formClient.errors[propertyName] = JSON.stringify(passwordValidationType);
      setPasswordValidations(passwordValidationType);
    }
  }, [formClient, propertyName, selectedView, passwordValidations]);

  return (
    <ManifestUI.Box css={{ marginTop: spaceLarge }}>
      <ManifestUI.Flex
        gap="small"
        orientation="vertical"
        justify="center"
        css={{
          height: '104px',
          backgroundColor: '#F4F4F5',
          padding: '24px',
          marginLeft: `calc(0px - ${spaceLarge})`,
          marginRight: `calc(0px - ${spaceLarge})`,
          marginTop: `calc(0px - ${spaceLarge})`,
          width: `calc(100% + ${spaceLarge} + ${spaceLarge})`,
          marginBottom: spaceSmall,
        }}
      >
        <ManifestUI.RadioGroup
          {...radioGroupProps}
          aria-label={'radioProps'}
          aria-labelledby={'radioProps'}
          onChange={(value: string): void => {
            userPasswordComponent.handleRadioGroupOnChange({
              value,
              formClient,
              propertyName,
              setSelectedView,
              setPasswordValidations,
            });
          }}
          value={selectedView}
          children={radioButtons.map((radioProps) => {
            return (
              <ManifestUI.Radio
                aria-label={radioProps.value}
                aria-labelledby={radioProps.value}
                key={String(radioProps.value)}
                {...radioProps}
              />
            );
          })}
        />
      </ManifestUI.Flex>
      {selectedView === 'setPassword' && (
        <div style={{ position: 'relative' }}>
          <HTMLFormElementsFactory.composeTextInputField
            {...userPasswordComponent.additionalTextInputFieldProps}
            data-testid={propertyName}
            aria-labelledby={propertyName}
            aria-label={propertyName}
            name={propertyName}
            id={propertyName}
            css={{
              marginBottom: designTokens.spaceSmall,
            }}
            value={formClient.values[propertyName]}
            inputProps={{
              type: showPassword ? 'text' : 'password',
              style: { position: 'relative' },
            }}
            isDisabled={!userPasswordComponent.isEditable || isLoading}
            isReadOnly={!userPasswordComponent.isEditable}
            isRequired={true}
            validationState={
              Object.values(passwordValidations).includes('fail')
                ? 'invalid'
                : 'valid'
            }
            onBlur={() => {
              userPasswordComponent.onChangeHandler(
                formClient.values[propertyName],
                true,
                propertyName,
                formClient,
                [passwordValidations, setPasswordValidations],
              );
            }}
            onChange={(value) => {
              userPasswordComponent.onChangeHandler(
                value,
                false,
                propertyName,
                formClient,
                [passwordValidations, setPasswordValidations],
              );
            }}
          />
          <RenderedIcon
            css={{
              position: 'absolute',
              right: '0.6rem',
              top: `2.1rem`,
              cursor: 'pointer',
            }}
            data-testid={'show-password-toggle'}
            onClick={() => setShowPassword(!showPassword)}
          />
          <SetPasswordInstructions {...{ passwordValidations }} />
        </div>
      )}
    </ManifestUI.Box>
  );
};
